import React, { Component } from 'react';

import Constants from '../../Constants';
import Seasons from '../../Seasons';
import Utils from '../../Utils';

import '../../styles/modals/ScheduleModal.css';

// Every noun the schedule counts pluralises with an "s", so this is the whole
// of it. It exists because a day lists only what applies to it, and a count of
// one is the common case -- "1 holds" would read as a bug in the schedule.
const count = (n, noun) => `${n} ${noun}${n === 1 ? "" : "s"}`;

class ScheduleModal extends Component {
  // What a day is worth saying about, in the order it reads best: how big the
  // board is and how much choice a turn gives you, then the one twist that
  // defines the day, then what the day hands you to offset it. Anything sitting
  // at its default is left out entirely -- that is the point of the list over
  // the old grid of mostly-zero columns, so do not be tempted to spell out an
  // absent feature here.
  //
  // Letter options are the one exception, and are listed even at their default.
  // They are not a feature a day either has or lacks: every day gives you some
  // number of letters to choose between, and how much choice a turn offers is
  // as much a part of a day's shape as how big its board is. Reading it off
  // every line is what makes the difference between a two-option day and a
  // three-option one visible, which is exactly what an omitted default hides.
  //
  // They come *before* the mode, ahead of everything optional, for the same
  // reason the day name gets a column of its own: the two things every day has
  // are the two things every line opens with, so the eye can run down a fixed
  // prefix instead of meeting a mode name on one row and a letter count on the
  // next. Put the mode back in front and only the days with one line up.
  getDetails = (parameters) => {
    const details = [`${parameters.rows}x${parameters.columns}`];

    // An increment day has no single count to give -- it cycles 1-2-3 -- so it
    // names the range instead, taken from `letterOptionCount` rather than a
    // literal 3. That field is the width of the day's deal, and the deal is
    // three to a round on an increment day precisely because three is the
    // widest a round of one can get.
    details.push(parameters.incrementMode
      ? `1–${parameters.letterOptionCount} letter options`
      : count(parameters.letterOptionsPerRound, "letter option"));

    if (parameters.adjacencyMode) {
      details.push(Constants.MODE_NAME_ADJACENCY);
    }
    if (parameters.incrementMode) {
      details.push(Constants.MODE_NAME_INCREMENT);
    }
    if (parameters.dropMode) {
      details.push(Constants.MODE_NAME_DROP);
    }
    if (parameters.slideMode) {
      details.push(Constants.MODE_NAME_SLIDE);
    }
    if (parameters.wraparoundMode) {
      details.push(Constants.MODE_NAME_WRAPAROUND);
    }

    if (parameters.startLetterCount) {
      details.push(count(parameters.startLetterCount, "starting letter"));
    }
    if (parameters.expansionCount) {
      details.push(count(parameters.expansionCount, "expanded cell"));
    }
    if (parameters.bonusCellCount) {
      details.push(count(parameters.bonusCellCount, "bonus cell"));
    }
    if (parameters.previewCount) {
      details.push(count(parameters.previewCount, "letter preview"));
    }
    if (parameters.holdCount) {
      details.push(count(parameters.holdCount, "hold"));
    }

    return details;
  }

  render() {
    // Until the first season begins there is no banner, and the schedule stands
    // on its own the way it did before seasons existed.
    const status = Seasons.getSeasonStatus(Utils.getDateStringToday());
    const season = Seasons.getSeason(status.number);
    // A season is six whole Monday-anchored weeks, so the week in progress
    // never straddles a boundary and always describes one schedule.
    const weekStart = Utils.getDateStringMonday(Utils.getDateStringToday());

    return (
      <div className="schedule-modal">
        { status.started &&
          <div className="schedule-modal-season">
            Season {season.number} · {season.startDate} to {season.endDate}
          </div>
        }
        <div className="schedule-modal-days">
          {/* Read off the week the schedule actually holds, Monday first, so
              this cannot drift from what the daily is dealt. */}
          {Constants.DAYS.map((dayName, i) => {
            const day = (i + 1) % 7;
            const date = Utils.addDays(weekStart, i);
            const parameters = Seasons.getDailyParameters(date);

            return (
              <div className={`schedule-modal-day schedule-modal-day-${i % 2 === 0 ? "even" : "odd"}`} key={`schedule_day_${i}`}>
                <div className="schedule-modal-day-name">{Constants.DAYS[day]}</div>
                <div className="schedule-modal-day-details">
                  {this.getDetails(parameters).join(" · ")}
                </div>
              </div>
            );
          })}
        </div>

        <button className="schedule-modal-close" onClick={this.props.onClose}>
          Close
        </button>
      </div>
    );
  }
}

export default ScheduleModal;
